/**
 * CashflowLayout — Shell for all cashflow pages.
 *
 * Provides the top-level KPI ribbon, page title, and wraps children
 * inside the platform Layout. The sidebar navigation is handled by
 * the platform's SectionConfig (cashflow section in sectionConfigs.ts).
 */

import React from 'react';
import Layout from '../../components/layout/Layout';
import { useLocation } from 'react-router-dom';
import { cn } from '../../shared/lib/cn';
import { Button } from '../../components/ui';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { useForecastSummary, useRefreshAllCashflowData } from './api';
import { formatCurrency, liquidityBg, computeKPIs, formatRetrievedAt } from './utils';
import type { LiquidityStatus } from './types';

const PAGE_TITLES: Record<string, string> = {
  '/cashflow': 'Forecast Summary',
  '/cashflow/ceo': 'Summary Dashboard',
  '/cashflow/receipts': 'Receipts Detail',
  '/cashflow/disbursements': 'Disbursements Detail',
  '/cashflow/scenarios': 'Scenario Analysis',
  '/cashflow/scenario-modeling': 'Scenario Modeling',
  '/cashflow/risk-register': 'Cash Flow Risk Register',
  '/cashflow/variance': 'Forecast vs. Actuals',
  '/cashflow/configuration': 'Cash Flow Configuration',
  '/cashflow/availability': 'Cash Availability & Truth',
  '/cashflow/variance-intelligence': 'Variance Intelligence Copilot',
  '/cashflow/monthly-rollup': 'Weekly-to-Monthly Rollup',
  '/cashflow/budget-engine': 'Budget-to-Cash Engine',
  '/cashflow/fpa-feedback': 'Cash-to-FP&A Feedback',
  '/cashflow/working-capital': 'Working Capital Actions',
  '/cashflow/drift-monitor': 'Bank-to-Forecast Drift Monitor',
  '/cashflow/monitors': 'Operational Monitors',
};

export function CashflowLayout({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const title = PAGE_TITLES[pathname] ?? '13-Week Cash Flow';
  const { data: summary, isLoading } = useForecastSummary();
  const refreshAll = useRefreshAllCashflowData();

  const kpis = summary?.weeks ? computeKPIs(summary.weeks) : null;
  const lastWeek = summary?.weeks?.[summary.weeks.length - 1];
  const currentStatus: LiquidityStatus = lastWeek?.liquidity_status ?? 'GREEN';

  return (
    <Layout>
      <div className="flex flex-col h-full">
        {/* KPI Ribbon */}
        {!isLoading && kpis && (
          <div className="border-b border-gray-200 dark:border-dark-border bg-white dark:bg-dark-surface px-6 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-6">
                <div>
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Ending Cash W13
                  </span>
                  <p className="text-lg font-semibold text-charcoal dark:text-gray-100">
                    {formatCurrency(kpis.ending_cash_week13, true)}
                  </p>
                </div>
                <div className="h-8 w-px bg-gray-200 dark:bg-dark-border" />
                <div>
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Min Cash (W{kpis.minimum_cash_week})
                  </span>
                  <p className="text-lg font-semibold text-charcoal dark:text-gray-100">
                    {formatCurrency(kpis.minimum_cash_amount, true)}
                  </p>
                </div>
                <div className="h-8 w-px bg-gray-200 dark:bg-dark-border" />
                <div>
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Net 13W
                  </span>
                  <p className={cn(
                    'text-lg font-semibold',
                    kpis.net_cash_flow_13w >= 0
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : 'text-red-600 dark:text-red-400',
                  )}>
                    {formatCurrency(kpis.net_cash_flow_13w, true)}
                  </p>
                </div>
                <div className="h-8 w-px bg-gray-200 dark:bg-dark-border" />
                <div>
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Runway
                  </span>
                  <p className="text-lg font-semibold text-charcoal dark:text-gray-100">
                    {kpis.weeks_of_runway > 26 ? '26+' : kpis.weeks_of_runway.toFixed(1)} wks
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <span className={cn(
                  'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                  liquidityBg(currentStatus),
                )}>
                  {currentStatus}
                </span>
                {summary && (
                  <div className="text-right text-xs text-gray-400 dark:text-gray-500">
                    <div>v{summary.version} &middot; {summary.status}</div>
                    <div>{formatRetrievedAt(summary.retrieved_at)}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Page Header */}
        <div className="px-6 pt-5 pb-2 flex items-center justify-between gap-4">
          <h1 className="text-xl font-semibold text-charcoal dark:text-gray-100">
            {title}
          </h1>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => refreshAll.mutate()}
            disabled={refreshAll.isPending}
            title="Refresh all cashflow applet data"
          >
            <ArrowPathIcon className={cn(refreshAll.isPending ? 'animate-spin' : '')} />
            {refreshAll.isPending ? 'Refreshing all...' : 'Refresh all'}
          </Button>
        </div>

        {/* Page Content */}
        <div className="flex-1 overflow-auto px-6 pb-6">
          {children}
        </div>
      </div>
    </Layout>
  );
}

export default CashflowLayout;
